/**
 * 参数变量替换工具。
 *
 * 套件作者在 YAML 中用 `${var}` 占位（如 `keywords: ['Started ${package}']`），
 * UI 收集到值之后调用 applyParams 渲染出最终可用的 Suite 对象。
 *
 * 关键约束：
 *   1. 只替换字符串字段。matcher / severity / id / name 等结构性字段也允许包含
 *      `${var}`，但若结果为空 / 含非法字符仍可能在 SuiteRunner 构造时抛错。
 *   2. 第一版引擎已下线 regex matcher 与 step.pattern，所有匹配字段都按
 *      字面字符串处理；这里保留 `regex` 转义钩子（REGEX_FIELDS 当前为空）
 *      以便将来引入模式字段时不必动递归骨架。
 *   3. 缺少必填或类型不匹配 -> 抛 ParamError，UI 把它映射到表单红字。
 */

import type { ParamSpec, Suite } from './types';

export class ParamError extends Error {
  readonly errors: string[];
  constructor(errors: string[]) {
    super(errors.join('; '));
    this.name = 'ParamError';
    this.errors = errors;
  }
}

const VAR_RE = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

/** 在任意 JSON 树中收集所有 `${var}` 引用的变量名。 */
export function collectVars(value: unknown): Set<string> {
  const out = new Set<string>();
  const walk = (v: unknown): void => {
    if (typeof v === 'string') {
      let m: RegExpExecArray | null;
      VAR_RE.lastIndex = 0;
      while ((m = VAR_RE.exec(v)) !== null) {
        out.add(m[1]);
      }
      return;
    }
    if (Array.isArray(v)) {
      v.forEach(walk);
      return;
    }
    if (v && typeof v === 'object') {
      for (const k of Object.keys(v)) walk((v as Record<string, unknown>)[k]);
    }
  };
  walk(value);
  return out;
}

/** 校验输入的 values 是否满足 Suite.parameters 声明，并补默认值。 */
export function validateParams(
  specs: ParamSpec[] | undefined,
  values: Record<string, string | number | boolean> | undefined,
): Record<string, string | number | boolean> {
  const errs: string[] = [];
  const out: Record<string, string | number | boolean> = {};
  const v = values ?? {};
  const declaredNames = new Set<string>();

  for (const spec of specs ?? []) {
    declaredNames.add(spec.name);
    let raw = (v as Record<string, unknown>)[spec.name];
    if (raw === undefined || raw === '') {
      if (spec.default !== undefined) raw = spec.default;
    }
    if (raw === undefined || raw === '') {
      if (spec.required) {
        errs.push(`参数 \`${spec.name}\` 必填`);
      }
      continue;
    }
    switch (spec.type) {
      case 'string': {
        const s = String(raw);
        if (spec.pattern) {
          try {
            const re = new RegExp(spec.pattern);
            if (!re.test(s)) {
              errs.push(`参数 \`${spec.name}\` 不匹配 pattern: ${spec.pattern}`);
              continue;
            }
          } catch {
            errs.push(`参数 \`${spec.name}\` 的 pattern 无效: ${spec.pattern}`);
            continue;
          }
        }
        out[spec.name] = s;
        break;
      }
      case 'number': {
        const n = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(n)) {
          errs.push(`参数 \`${spec.name}\` 必须是数字`);
          continue;
        }
        out[spec.name] = n;
        break;
      }
      case 'boolean': {
        if (typeof raw === 'boolean') {
          out[spec.name] = raw;
        } else if (raw === 'true' || raw === '1') {
          out[spec.name] = true;
        } else if (raw === 'false' || raw === '0') {
          out[spec.name] = false;
        } else {
          errs.push(`参数 \`${spec.name}\` 必须是 boolean`);
        }
        break;
      }
    }
  }

  // 多余字段允许，但提示一下；做成 warning 比硬错好（YAML 复用时常见）
  for (const k of Object.keys(v)) {
    if (!declaredNames.has(k)) {
      out[k] = v[k] as string | number | boolean;
    }
  }

  if (errs.length > 0) throw new ParamError(errs);
  return out;
}

/** Regex special-character escape，避免用户输入 `com.foo.bar` 击穿正则。 */
export function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 用变量值渲染所有 `${var}`。regexPaths 标记哪些字段需要正则转义。
 * 当字段值整体只是一个 `${var}`（无其他文字）时，对于 number/boolean
 * 类型的变量保留其类型，便于后续逻辑判断（M1 中暂不依赖此特性）。
 */
function renderString(
  s: string,
  values: Record<string, string | number | boolean>,
  regexEscape: boolean,
): string {
  return s.replace(VAR_RE, (full, varName) => {
    if (!(varName in values)) return full;
    const raw = String(values[varName]);
    return regexEscape ? escapeRegex(raw) : raw;
  });
}

interface ApplyState {
  values: Record<string, string | number | boolean>;
  /** 当前路径需要正则转义；递归继承。 */
  regex: boolean;
}

/**
 * 字段名集合：进入这些 key 时，递归内的字符串值视为正则字段。
 *
 * 第一版引擎删除了 regex matcher 与 step.pattern，正则字段集合理应为空；
 * 保留这个 hook 是为了将来若有"模式片段"类字段重新引入时不必动 applyParams
 * 的递归骨架。
 */
const REGEX_FIELDS = new Set<string>();

function applyToValue(value: unknown, state: ApplyState): unknown {
  if (typeof value === 'string') {
    return renderString(value, state.values, state.regex);
  }
  if (Array.isArray(value)) {
    return value.map((item) => applyToValue(item, state));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      const childRegex = state.regex || REGEX_FIELDS.has(k);
      out[k] = applyToValue(v, { values: state.values, regex: childRegex });
    }
    return out;
  }
  return value;
}

/**
 * 把 `${var}` 替换到 Suite 各字段中。返回一个全新对象，不修改原 suite。
 */
export function applyParams(
  suite: Suite,
  values: Record<string, string | number | boolean>,
): Suite {
  const validated = validateParams(suite.parameters, values);
  return applyToValue(suite, { values: validated, regex: false }) as Suite;
}
